import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PencilSquare,
  Key,
  Building,
  Envelope,
  Telephone,
  GeoAlt,
  ClockHistory,
  PersonCircle,
  Clipboard,
} from "react-bootstrap-icons";
import "./styles/view-details.css";
import { useAuth } from "../../contexts/AuthContext";

export default function ViewDetails() {
  const { auth } = useAuth();
  const token = auth?.token;
  const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
  const navigate = useNavigate();
  const abortRef = useRef(null);

  const [business, setBusiness] = useState(null);
  const [businessError, setBusinessError] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  useEffect(() => {
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    setLoading(true);
    setError("");

    // (async () => {
    //   try {
    //     const res = await fetch(`${USER_API}/sellers/me`, {
    //       method: "GET",
    //       headers: {
    //         Authorization: `Bearer ${token}`,
    //       },
    //       signal,
    //     });

    //     const contentType = res.headers.get("content-type") || "";
    //     const text = await res.text();

    //     //  Prevent HTML crash
    //     if (
    //       contentType.includes("text/html") ||
    //       text.trim().startsWith("<!DOCTYPE")
    //     ) {
    //       console.error("HTML response:", text.slice(0, 300));
    //       throw new Error("Server returned HTML instead of JSON");
    //     }

    //     const data = text ? JSON.parse(text) : {};

    //     if (!res.ok) {
    //       throw new Error(data.message || `Error ${res.status}`);
    //     }

    //     // Map backend → frontend
    //     setProfile({
    //       firstName: data.firstName,
    //       lastName: data.lastName,
    //       email: data.email,
    //       phone: data.phone,
    //       city: data.city,
    //       country: data.country,
    //       avatarUrl: data.avatarUrl,
    //       updatedAt: data.updatedAt,
    //       createdAt: data.createdAt,
    //     });
    //   } catch (err) {
    //     if (err.name === "AbortError") return;

    //     console.error(err);
    //     setError(err.message || "Failed to load profile");
    //   } finally {
    //     setLoading(false);
    //   }
    // })();

    (async () => {
      try {
        const res = await fetch(`${USER_API}/sellers/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        });

        const text = await res.text();
        const data = text ? JSON.parse(text) : {};

        if (!res.ok) throw new Error(data.message || `Error ${res.status}`);

        setProfile({
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          city: data.city,
          country: data.country,
          avatarUrl: data.avatarUrl,
          updatedAt: data.updatedAt,
          createdAt: data.createdAt,
        });
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message);
        }
      }

      // SECOND CALL (business profile)
      try {
        const res = await fetch(`${USER_API}/sellers/profile`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal,
        });

        const text = await res.text();
        const data = text ? JSON.parse(text) : {};

        if (!res.ok)
          throw new Error(data.message || "Failed to load business data");

        setBusiness(data);
      } catch (err) {
        if (err.name !== "AbortError") {
          setBusinessError(err.message);
        }
      } finally {
        setLoading(false);
      }
    })();

    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const onCopyEmail = useCallback(async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus(""), 2000);
    } catch {
      setCopyStatus("Copy failed");
      setTimeout(() => setCopyStatus(""), 2000);
    }
  }, []);

  const initials = profile
    ? `${profile.firstName?.[0] || ""}${profile.lastName?.[0] || ""}`.toUpperCase()
    : "";

  return (
    <div className="container py-3" style={{ maxWidth: 1100 }}>
      {/* HEADER */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h4 className="fw-semibold mb-1">Account Overview</h4>
          <div className="text-muted small">
            Manage your personal and business information
          </div>
        </div>

        <div className="d-flex gap-2">
          <Link
            to="edit"
            className="btn btn-primary btn-sm d-flex align-items-center gap-1"
          >
            <PencilSquare /> Edit
          </Link>
          <Link
            to="change-password"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1"
          >
            <Key /> Security
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="card shadow-sm p-4 text-center">
          <div className="spinner-border text-primary" />
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <div className="row g-4">
          {/* LEFT PROFILE */}
          <div className="col-lg-4">
            <div className="card shadow-sm text-center p-4">
              <div className="profile-avatar mb-3">
                {profile?.avatarUrl ? (
                  <img
                    alt="avatar"
                    src={profile.avatarUrl}
                    className="rounded-circle"
                  />
                ) : (
                  <div className="avatar-fallback rounded-circle">
                    <PersonCircle size={40} />
                  </div>
                )}
              </div>

              <h5 className="fw-semibold mb-1">
                {profile?.firstName} {profile?.lastName}
              </h5>

              <div className="text-muted small mb-3">
                Member since {new Date(profile?.createdAt).getFullYear()}
              </div>

              <div className="d-grid gap-2">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => navigate("edit")}
                >
                  <PencilSquare className="me-1" />
                  Edit Profile
                </button>

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate("change-password")}
                >
                  <Key className="me-1" />
                  Change Password
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="col-lg-8">
            {/* CONTACT */}
            <div className="card shadow-sm mb-4">
              <div className="card-body">
                <h6 className="section-title mb-3">Personal Information</h6>

                <div className="info-item">
                  <Envelope className="icon" />
                  <div>
                    <div className="label">Email</div>
                    <div className="value d-flex align-items-center gap-2">
                      {profile?.email}
                      <button
                        className="icon-btn"
                        onClick={() => onCopyEmail(profile.email)}
                      >
                        <Clipboard />
                      </button>
                      {copyStatus && (
                        <span className="text-success small">{copyStatus}</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="info-item">
                  <Telephone className="icon" />
                  <div>
                    <div className="label">Phone</div>
                    <div className="value">{profile?.phone || "-"}</div>
                  </div>
                </div>

                <div className="info-item">
                  <GeoAlt className="icon" />
                  <div>
                    <div className="label">Location</div>
                    <div className="value">
                      {profile?.city}, {profile?.country}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* SECURITY */}
            <div className="card shadow-sm mb-4">
              <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <div className="label">Last password update</div>
                  <div className="value">
                    <ClockHistory className="me-1" />
                    {new Date(profile?.updatedAt).toLocaleString()}
                  </div>
                </div>

                <Link
                  to="change-password"
                  className="btn btn-sm btn-outline-primary"
                >
                  Update
                </Link>
              </div>
            </div>

            {/* BUSINESS */}
            <div className="card shadow-sm">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="business-logo">
                    {business?.storeLogoPath ? (
                      <img
                        src={business.storeLogoPath}
                        alt="Company Logo"
                        className="rounded-circle"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = ""; // fallback to placeholder
                        }}
                      />
                    ) : (
                      <div className="logo-placeholder rounded-circle">
                        <Building size={18} />
                      </div>
                    )}
                  </div>

                  <h6 className="section-title mb-0">Business Information</h6>

                  {business && (
                    <span
                      className={`status-badge ${
                        business.verificationStatus === "APPROVED"
                          ? "approved"
                          : business.verificationStatus === "REJECTED"
                            ? "rejected"
                            : "pending"
                      }`}
                    >
                      {business.verificationStatus}
                    </span>
                  )}
                </div>

                {businessError ? (
                  <div className="alert alert-warning">{businessError}</div>
                ) : !business ? (
                  <div className="text-muted small">Loading...</div>
                ) : (
                  <div className="info-grid">
                    <div className="info-box">
                      <div className="label">Store</div>
                      <div className="value">{business.storeName}</div>
                    </div>

                    <div className="info-box">
                      <div className="label">Company</div>
                      <div className="value">{business.companyName}</div>
                    </div>

                    <div className="info-box">
                      <div className="label">TIN</div>
                      <div className="value">{business.tinNumber}</div>
                    </div>

                    <div className="info-box">
                      <div className="label">Business Email</div>
                      <div className="value">{business.businessEmail}</div>
                    </div>

                    <div className="info-box">
                      <div className="label">Business Phone</div>
                      <div className="value">{business.businessPhone}</div>
                    </div>

                    {business.businessAddress && (
                      <div className="info-box">
                        <div className="label">Address</div>
                        <div className="value">
                          {business.businessAddress.street},{" "}
                          {business.businessAddress.city},{" "}
                          {business.businessAddress.country}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {business?.verificationNotes && (
                  <div className="alert alert-danger mt-3 small">
                    {business.verificationNotes}
                  </div>
                )}

                {/* DOCUMENTS */}
                <div className="mt-3">
                  <div className="label mb-1">Documents</div>
                  <div className="d-flex gap-3 flex-wrap">
                    {business?.businessLicensePath && (
                      <a
                        href={business.businessLicensePath}
                        target="_blank"
                        rel="noreferrer"
                      >
                        License
                      </a>
                    )}
                    {business?.tinCertificatePath && (
                      <a
                        href={business.tinCertificatePath}
                        target="_blank"
                        rel="noreferrer"
                      >
                        TIN Certificate
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
